import { BaseService } from '@/chassis/data/services/base-service';
import { BaseFormService, ValidationRules } from '@/chassis/data/services/base-form-service';
import { createNormalizer, FieldTransforms } from '@/chassis/data/services/normalizer-utils';

export interface PublicAgencyRecord {
    id: string;
    name: string;
    cnpj: string;
    siorgCode?: string;
    pncpUnitCode?: string;
    administrativeSphere: string;
    agencyType: string;
    parentAgencyId?: string;
    address?: string;
    zipCode?: string;
    city?: string;
    cityCode?: string;
    state?: string;
    municipality?: string;
    website?: string;
    email?: string;
    phone?: string;
    /** Logotipo do órgão (data URL base64) — usado no cabeçalho dos relatórios. */
    logoBase64?: string;
    creationDate?: string;
    active?: boolean;
    createdAt?: string;
    updatedAt?: string;
    createdBy?: string;
    createdByName?: string;
    createdByUser?: string;
    updatedBy?: string;
    updatedByName?: string;
    updatedByUser?: string;
}

export interface PublicAgencyFilters {
    searchTerm?: string;
    administrativeSphere?: string;
    agencyType?: string;
    city?: string;
    state?: string;
    active?: string;
    page?: number;
    limit?: number;
}

export type PublicAgencyFormData = Omit<PublicAgencyRecord, 'id'>;

export const resolveActiveFlag = (record: Partial<Pick<PublicAgencyRecord, 'active'>>) => {
    if (typeof record.active === 'boolean') return record.active;
    return true;
};

export const normalizePublicAgencyRecord = createNormalizer<PublicAgencyRecord>({
    idFields: ['parentAgencyId'],
    fields: [
        'name',
        'cnpj',
        'siorgCode',
        'pncpUnitCode',
        'administrativeSphere',
        'agencyType',
        'parentAgencyId',
        'address',
        'zipCode',
        'city',
        'cityCode',
        'state',
        'municipality',
        'website',
        'email',
        'phone',
        'logoBase64',
        { target: 'creationDate', transform: FieldTransforms.identity },
        { target: 'active', transform: FieldTransforms.toBoolean },
        { target: 'createdAt', transform: FieldTransforms.identity },
        { target: 'updatedAt', transform: FieldTransforms.identity },
        'createdBy',
        'createdByName',
        'createdByUser',
        'updatedBy',
        'updatedByName',
        'updatedByUser',
    ],
    customize: (normalized) => {
        const resolvedActive = resolveActiveFlag(normalized);
        normalized.active = resolvedActive;
    },
});

class PublicAgenciesService extends BaseService<PublicAgencyRecord, PublicAgencyFormData, Partial<PublicAgencyFormData>, PublicAgencyFilters> {
    constructor() {
        super({
            basePath: '/publicagencies',
            normalizeRecord: normalizePublicAgencyRecord,
        });
    }
}

export const publicAgenciesService = new PublicAgenciesService();

const publicAgencyFormDefaults: PublicAgencyFormData = {
    name: '',
    cnpj: '',
    siorgCode: '',
    pncpUnitCode: '',
    administrativeSphere: '',
    agencyType: '',
    parentAgencyId: '',
    address: '',
    zipCode: '',
    city: '',
    cityCode: '',
    state: '',
    municipality: '',
    website: '',
    email: '',
    phone: '',
    logoBase64: '',
    creationDate: '',
    active: true,
    createdAt: undefined,
    updatedAt: undefined,
    createdBy: undefined,
    createdByName: undefined,
    createdByUser: undefined,
    updatedBy: undefined,
    updatedByName: undefined,
    updatedByUser: undefined,
};

const toTrimmedString = (value?: string | null) => {
    if (typeof value !== 'string') {
        return undefined;
    }

    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
};

const sanitizePublicAgencyPayload = (
    formData: Partial<PublicAgencyFormData>,
): Partial<PublicAgencyFormData> => {
    const {
        createdAt,
        updatedAt,
        createdBy,
        createdByName,
        createdByUser,
        updatedBy,
        updatedByName,
        updatedByUser,
        ...submittable
    } = formData;

    const payload: Partial<PublicAgencyFormData> = {};

    payload.name = (submittable.name ?? '').trim();
    payload.cnpj = (submittable.cnpj ?? '').trim();
    payload.administrativeSphere = (submittable.administrativeSphere ?? '').trim();
    payload.agencyType = (submittable.agencyType ?? '').trim();

    if ('siorgCode' in submittable) {
        payload.siorgCode = toTrimmedString(submittable.siorgCode) ?? '';
    }

    if ('pncpUnitCode' in submittable) {
        payload.pncpUnitCode = toTrimmedString(submittable.pncpUnitCode) ?? '';
    }

    if ('parentAgencyId' in submittable) {
        payload.parentAgencyId = toTrimmedString(submittable.parentAgencyId) ?? '';
    }

    if ('address' in submittable) {
        payload.address = toTrimmedString(submittable.address) ?? '';
    }

    if ('zipCode' in submittable) {
        payload.zipCode = toTrimmedString(submittable.zipCode) ?? '';
    }

    if ('city' in submittable) {
        payload.city = toTrimmedString(submittable.city) ?? '';
    }

    if ('cityCode' in submittable) {
        payload.cityCode = toTrimmedString(submittable.cityCode) ?? '';
    }

    if ('state' in submittable) {
        payload.state = (toTrimmedString(submittable.state) ?? '').toUpperCase();
    }

    if ('municipality' in submittable) {
        payload.municipality = toTrimmedString(submittable.municipality) ?? '';
    }

    if ('website' in submittable) {
        payload.website = toTrimmedString(submittable.website) ?? '';
    }

    if ('email' in submittable) {
        payload.email = toTrimmedString(submittable.email) ?? '';
    }

    if ('phone' in submittable) {
        payload.phone = toTrimmedString(submittable.phone) ?? '';
    }

    if ('logoBase64' in submittable) {
        // Logotipo do órgão (data URL base64) — persistido no backend para uso
        // no cabeçalho dos relatórios. String vazia limpa o logo.
        payload.logoBase64 = typeof submittable.logoBase64 === 'string' ? submittable.logoBase64 : '';
    }

    if ('creationDate' in submittable) {
        payload.creationDate = toTrimmedString(submittable.creationDate) ?? '';
    }

    const activeFlag = resolveActiveFlag(submittable);
    payload.active = activeFlag;
    payload.active = activeFlag;

    return payload;
};

export const createPublicAgenciesFormService = () =>
    new BaseFormService<
        PublicAgencyRecord,
        PublicAgencyFormData,
        PublicAgencyFormData,
        Partial<PublicAgencyFormData>,
        PublicAgencyFilters
    >({
        basePath: '/publicagencies',
        initialData: publicAgencyFormDefaults,
        normalizeRecord: normalizePublicAgencyRecord,
        validationRules: {
            name: [ValidationRules.required('Nome oficial é obrigatório')],
            cnpj: [ValidationRules.required('CNPJ é obrigatório')],
            administrativeSphere: [ValidationRules.required('Esfera administrativa é obrigatória')],
            agencyType: [ValidationRules.required('Tipo de órgão é obrigatório')],
            address: [ValidationRules.required('Endereço é obrigatório')],
            zipCode: [ValidationRules.required('CEP é obrigatório')],
            city: [ValidationRules.required('Cidade é obrigatória')],
            cityCode: [ValidationRules.required('Código do município (IBGE) é obrigatório')],
            state: [
                ValidationRules.required('Estado é obrigatório'),
                ValidationRules.pattern(/^[A-Z]{2}$/, 'Estado deve conter 2 letras (ex: SC, PR, SP)'),
            ],
        },
        transformForCreate: (formData) => sanitizePublicAgencyPayload(formData) as PublicAgencyFormData,
        transformForUpdate: (formData: Partial<PublicAgencyFormData>) => sanitizePublicAgencyPayload(formData),
        transformToForm: (record) => ({
            ...publicAgencyFormDefaults,
            name: record.name || '',
            cnpj: record.cnpj || '',
            siorgCode: record.siorgCode || '',
            pncpUnitCode: record.pncpUnitCode || '',
            administrativeSphere: record.administrativeSphere || '',
            agencyType: record.agencyType || '',
            parentAgencyId: record.parentAgencyId || '',
            address: record.address || '',
            zipCode: record.zipCode || '',
            city: record.city || '',
            cityCode: record.cityCode || '',
            state: (record.state || '').toUpperCase(),
            municipality: record.municipality || '',
            website: record.website || '',
            email: record.email || '',
            phone: record.phone || '',
            creationDate: record.creationDate || '',
            active: resolveActiveFlag(record),
            createdAt: record.createdAt,
            updatedAt: record.updatedAt,
            createdBy: record.createdBy,
            createdByName: record.createdByName,
            createdByUser: record.createdByUser,
            updatedBy: record.updatedBy,
            updatedByName: record.updatedByName,
            updatedByUser: record.updatedByUser,
        }),
    });

export type PublicAgency = PublicAgencyRecord;
